import { useState, useEffect, useContext, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";

import Link from "../atoms/mui/Link";

import { AnimatedLogo } from "@aclymatepackages/atoms";

import SignInForm from "./SignInForm";

import AccountAccessLayout from "../layouts/AccountAccessLayout";

import { handleLoggedInUser } from "../../helpers/components/accountAccess";
import { AccountAccessContext } from "../../helpers/contexts/accountAccess";
import { useAuth } from "../../helpers/firebase";
import { analyticsPage } from "../../helpers/analytics";
import { getUrlSearchParams } from "../../helpers/otherHelpers";

// #375: the login page's ?redirect= value is attacker-controllable (it's
// read straight off the URL a visitor clicked), so only ever follow it when
// it's an unambiguous same-origin relative path -- never an absolute URL or
// a protocol-relative/backslash form a browser could resolve off-origin.
// Control characters are rejected outright because URL parsers strip
// tabs/newlines (e.g. "/\n/evil.example" resolves as "//evil.example").
// eslint-disable-next-line no-control-regex
const URL_CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;

const isSafeRedirectTarget = (target) =>
  typeof target === "string" &&
  !URL_CONTROL_CHARACTERS.test(target) &&
  target.startsWith("/") &&
  !target.startsWith("//") &&
  !target.startsWith("/\\");

const SignInBlock = () => {
  const [loggedInUser, loggedInUserLoading] = useAuth();
  const { setAccountAccessError } = useContext(AccountAccessContext);

  const [userEmail, setUserEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);

  const redirectParam = getUrlSearchParams("redirect");
  const decodedRedirectParam = redirectParam
    ? decodeURIComponent(redirectParam)
    : undefined;
  const postLoginUrl = isSafeRedirectTarget(decodedRedirectParam)
    ? decodedRedirectParam
    : undefined;

  const onLogin = useCallback(
    (user) =>
      handleLoggedInUser({
        user,
        setRedirect,
        setLoginLoading,
        setAccountAccessError,
        ...(postLoginUrl ? { platformUrl: postLoginUrl } : {})
      }),
    [setAccountAccessError, postLoginUrl]
  );

  useEffect(() => {
    if (!loggedInUserLoading && loggedInUser?.uid && !redirect && !loginLoading) {
      onLogin(loggedInUser);
    }
  }, [loggedInUser, redirect, loginLoading, loggedInUserLoading, onLogin]);

  const renderData = () => {
    if (loggedInUserLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <AnimatedLogo />
        </Box>
      );
    }

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    return (
      <SignInForm
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        password={password}
        setPassword={setPassword}
        onLogin={onLogin}
        setAccountAccessError={setAccountAccessError}
        loginLoading={loginLoading}
        setLoginLoading={setLoginLoading}
        setRedirect={setRedirect}
        cta={
          <Grid
            item
            container
            direction="column"
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <Typography variant="body1" display="inline">
                First time here?
              </Typography>
            </Grid>
            <Grid item>
              <Link href="/account-creation/company">
                <Typography variant="body2">
                  Create a company account
                </Typography>
              </Link>
            </Grid>
          </Grid>
        }
      />
    );
  };

  return <>{renderData()}</>;
};

const Login = () => {
  useEffect(() => {
    analyticsPage("login");
  }, []);

  return (
    <AccountAccessLayout
      formTitle="Welcome back"
      formSubtitle="Log back in to your account to keep saving the world."
      layoutType="login"
    >
      <SignInBlock />
    </AccountAccessLayout>
  );
};
export default Login;
